import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Table, 
  Card, 
  Button, 
  Tag, 
  Space,
  Modal,
  Tooltip,
  Descriptions,
  Divider,
  Typography,
  Spin,
  message,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic
} from 'antd';
import { 
  GiftOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  QrcodeOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { voucherService, Voucher } from '../../services/voucherService';
import { agencyService, Agency } from '../../services/agencyService';
import { agencyVoucherService } from '../../services/agencyVoucherService';
import moment from 'moment';
import type { ColumnType } from 'antd/es/table';
import type { Moment } from 'moment';

const { Option } = Select;
const { Text } = Typography;

interface AdminVoucherEsimProfile {
  simSerial?: string;
  activationCode?: string;
  smdpAddress?: string;
  matchingId?: string;
}

interface AdminVoucher extends Voucher {
  token?: string;
  amount?: number;
  creationDateTime?: Date | string;
  expirationDateTime?: Date | string;
  esimProfile?: AdminVoucherEsimProfile;
}

interface VoucherFormValues {
  value: number;
  agencyId: number;
  expirationDate: Moment;
}

interface VoucherStats {
  totalVouchers: number;
  activeVouchers: number;
  redeemedValue: number;
  inactiveVouchers?: number; // Optional for future use
}

const Vouchers = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [vouchers, setVouchers] = useState<AdminVoucher[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm<VoucherFormValues>();
  const [activateLoading, setActivateLoading] = useState<boolean>(false);
  const [qrModalVisible, setQrModalVisible] = useState<boolean>(false);
  const [activatedVoucher, setActivatedVoucher] = useState<AdminVoucher | null>(null);
  const [consumptionLoading, setConsumptionLoading] = useState<boolean>(false);
  const [consumptionModalVisible, setConsumptionModalVisible] = useState<boolean>(false);
  const [consumptionData, setConsumptionData] = useState<any>(null);
  const [stats, setStats] = useState<VoucherStats>({
    totalVouchers: 0,
    activeVouchers: 0,
    redeemedValue: 0, 
    inactiveVouchers: 0
  });

  const fetchVouchers = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await voucherService.getAll();
      const voucherData = (response.data || []) as AdminVoucher[];
      setVouchers(voucherData);
      calculateStats(voucherData);
    } catch (error) {
      message.error(t('admin.vouchers.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchAgencies = useCallback(async (): Promise<void> => {
    try {
      const response = await agencyService.getAll({
        page: 0,
        size: 10000
      });

      setAgencies(response.data.content); 
    } catch (error) {
      message.error(t('admin.vouchers.agenciesLoadError'));
    }
  }, [t]);

  useEffect(() => {
    fetchVouchers();
    fetchAgencies();
  }, [fetchVouchers, fetchAgencies]);

  const calculateStats = (data: AdminVoucher[]): void => {
    const stats = data.reduce<VoucherStats>((acc, curr) => ({
      totalVouchers: acc.totalVouchers + 1,
      activeVouchers: acc.activeVouchers + (curr.status === 'ACTIVE' ? 1 : 0),
      inactiveVouchers: acc.inactiveVouchers + (curr.status === 'EXPIRED' ? 1 : 0),
      redeemedValue: acc.redeemedValue + (curr.status === 'REDEEMED' ? 1 : 0)
    }), { totalVouchers: 0, activeVouchers: 0, redeemedValue: 0, inactiveVouchers: 0 });
    
    setStats(stats);
  };

  const handleCreate = async (_values: VoucherFormValues): Promise<void> => {
    try {
      //todo
      setIsModalVisible(false);
      form.resetFields();
      fetchVouchers();
    } catch (error) {
      message.error(t('admin.vouchers.createError'));
    }
  };

  const handleDeactivate = async (id: number): Promise<void> => {
    try {
      await voucherService.updateStatus(id, 'INACTIVE');
      message.success(t('admin.vouchers.deactivateSuccess'));
      fetchVouchers();
    } catch (error) {
      message.error(t('admin.vouchers.deactivateError'));
    }
  };

  const handleActivate = async (voucher: AdminVoucher): Promise<void> => {
    try {
      setActivateLoading(true);
      const voucherToken = voucher.token || voucher.code;

      if (!voucherToken) {
        message.error(t('agency.vouchers.activateError'));
        return;
      }

      const response = await agencyVoucherService.bookVoucher(voucherToken);
      setActivatedVoucher(response.data as AdminVoucher);
      setQrModalVisible(true);
      message.success(t('agency.vouchers.activateSuccess'));
      fetchVouchers();
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('agency.vouchers.activateError'));
    } finally {
      setActivateLoading(false);
    }
  };

  const handleViewConsumption = async (simSerial: string): Promise<void> => {
    try {
      setConsumptionLoading(true);
      const response = await agencyVoucherService.getConsumption(simSerial);
      setConsumptionData(response.data);
      setConsumptionModalVisible(true);
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('agency.vouchers.consumptionError'));
    } finally {
      setConsumptionLoading(false);
    }
  };

  const columns: ColumnType<AdminVoucher>[] = [
    { title: t('common.code'), dataIndex: 'token', key: 'token' },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (value: number, record: AdminVoucher) => `${value ?? record.value ?? 0} €`,
    },
    {
      title: t('common.status'),
      key: 'status',
      dataIndex: 'status',
    render: (status: string) => {
        const color =
          status === 'ACTIVE' ? 'green' :
          status === 'REDEEMED' ? 'blue' :
          status === 'EXPIRED' ? 'red' :
          status === 'REVOKED' ? 'volcano' :
          status === 'SENT' ? 'gold' :
          'default';
          
        return <Tag color={color}>{status}</Tag>;
    },

    },
    {
      title: t('common.agency'),
      dataIndex: ['agency', 'agencyName'],
      key: 'agencyName',
      render: (_: string, record: AdminVoucher) => {
                return record.agency ? record.agency.agencyName??  record.agency.id: 'N/A';
      },
    },
    {
      title: t('common.subscriber'),
      dataIndex: ['subscriber', 'name'],
      key: 'subscriberName',
      render: (_: string, record: AdminVoucher) => {
                return record.subscriber ? record.subscriber.firstName ?record.subscriber.firstName+' '+record.subscriber.lastName : record.subscriber.id: 'N/A';
      },
    },
    {
      title: t('common.creationDate'),
      dataIndex: 'creationDateTime',
      key: 'creationDateTime',
      render: (date: Date | string, record: AdminVoucher) => new Date(date || record.expirationDate).toLocaleDateString(),
    },
    {
      title: t('common.expirationDate'),
      dataIndex: 'expirationDateTime',
      key: 'expirationDateTime',
      render: (date: Date | string, record: AdminVoucher) => new Date(date || record.expirationDate).toLocaleDateString(),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: any, record: AdminVoucher) => (
        <Space>
          {(record.status === 'ACTIVE') && !record.esimProfile && (
            <Tooltip title={t('agency.vouchers.activateTooltip')}>
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
                loading={activateLoading}
                onClick={() => handleActivate(record)}
              >
                {t('agency.vouchers.activate')}
              </Button>
            </Tooltip>
          )}
          {(record.esimProfile || record.status === 'REDEEMED' ) && (
            <>
              <Tooltip title={t('agency.vouchers.viewQrCode')}>
                <Button
                  size="small"
                  icon={<QrcodeOutlined />}
                  onClick={() => {
                    setActivatedVoucher(record);
                    setQrModalVisible(true);
                  }}
                >
                  QR
                </Button>
              </Tooltip>
              <Tooltip title={t('agency.vouchers.viewConsumption')}>
                <Button
                  size="small"
                  icon={<BarChartOutlined />}
                  onClick={() => record.esimProfile?.simSerial && handleViewConsumption(record.esimProfile.simSerial)}
                >
                  {t('agency.vouchers.consumption')}
                </Button>
              </Tooltip>
            </>
          )}
          {record.status === 'ACTIVE' && (
            <Button 
              type="default" 
              danger 
              size="small"
              onClick={() => handleDeactivate(record.id)}
            >
              {t('common.deactivate')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('admin.vouchers.totalVouchers')}
              value={stats.totalVouchers}
              prefix={<GiftOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('admin.vouchers.activeVouchers')}
              value={stats.activeVouchers}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('admin.vouchers.redeemedValue')}
              value={stats.redeemedValue}
              prefix="€"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('admin.vouchers.expiredVouchers')}
              value={stats.inactiveVouchers}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

    <br/> <br/>
      <Card 
        title={t('admin.vouchers.cardTitle')}
        extra={<></>
          /*
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Nouveau Voucher
          </Button>*/
        }
      >
        <Table<AdminVoucher>
          columns={columns}
          dataSource={vouchers}
          loading={loading}
          rowKey="id"
        />
      </Card>

      <Modal
        title={t('agency.vouchers.qrCodeTitle')}
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={null}
      >
        {activatedVoucher?.esimProfile ? (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={t('agency.vouchers.simSerial')}>
                {activatedVoucher.esimProfile.simSerial || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('agency.vouchers.smdpAddress')}>
                {activatedVoucher.esimProfile.smdpAddress || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('agency.vouchers.matchingId')}>
                {activatedVoucher.esimProfile.matchingId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('agency.vouchers.activationCode')}>
                <Text copyable>{activatedVoucher.esimProfile.activationCode || '-'}</Text>
              </Descriptions.Item>
            </Descriptions>
          </>
        ) : (
          <Text type="secondary">{t('agency.vouchers.noEsimData')}</Text>
        )}
      </Modal>

      <Modal
        title={t('agency.vouchers.consumptionTitle')}
        open={consumptionModalVisible}
        onCancel={() => setConsumptionModalVisible(false)}
        footer={null}
      >
        {consumptionLoading ? (
          <Spin />
        ) : consumptionData ? (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={t('agency.vouchers.dataUsed')}>
                {consumptionData.dataUsed ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('agency.vouchers.dataLimit')}>
                {consumptionData.dataLimit ?? '-'}
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(consumptionData, null, 2)}
            </pre>
          </>
        ) : (
          <Text type="secondary">{t('agency.vouchers.noConsumptionData')}</Text>
        )}
      </Modal>

      <Modal
        title="Créer un Voucher"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form<VoucherFormValues>
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="value"
            label="Valeur (€)"
            rules={[{ required: true, message: 'La valeur est requise' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="agencyId"
            label="Agence"
            rules={[{ required: true, message: "L'agence est requise" }]}
          >
            <Select placeholder="Sélectionner une agence">
              {agencies.map(agency => (
                <Option key={agency.id} value={agency.id}>
                  {agency.agencyName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="expirationDate"
            label="Date d'expiration"
            rules={[{ required: true, message: "La date d'expiration est requise" }]}
          >
            <DatePicker 
              style={{ width: '100%' }}
              disabledDate={(current) => current && current < moment().endOf('day')}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Créer
            </Button>
          </Form.Item>
        </Form>
      </Modal>

    </>
  );
};

export default Vouchers;